import {
  Group,
  Text,
  Paper,
  Stack,
  ActionIcon,
  Menu,
  Avatar,
} from '@mantine/core'
import { IconDotsVertical, IconTrash } from '@tabler/icons-react'
import type { ChatMessageWithSender } from '@markku/types'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { AttachmentPreview } from './AttachmentPreview'
import { ChatHooks } from './hooks'

dayjs.extend(relativeTime)

interface MessageItemProps {
  message: ChatMessageWithSender
  isOwn: boolean
  showAuthor: boolean
  onDelete?: (messageId: number) => void
  chatHooks?: ChatHooks // Not currently used but kept for future extensibility
}

export function MessageItem({
  message,
  isOwn,
  showAuthor,
  onDelete,
}: MessageItemProps) {
  const isDeleted = !!message.deletedAt

  return (
    <Stack
      gap={showAuthor ? 'xs' : 0}
      align={isOwn ? 'flex-end' : 'flex-start'}
    >
      {/* Show author name above message when needed */}
      {showAuthor && message.sender && (
        <Group gap="xs" ml={!isOwn ? 44 : 0} mr={isOwn ? 44 : 0}>
          <Text size="xs" fw={600} c="dimmed">
            {message.sender.firstName} {message.sender.lastName}
          </Text>
        </Group>
      )}

      <Group
        align="flex-end"
        wrap="nowrap"
        justify={isOwn ? 'flex-end' : 'flex-start'}
        gap="xs"
      >
        {!isOwn && message.sender && (
          <Avatar color="blue" radius="xl" size={36}>
            {message.sender.firstName[0]}
            {message.sender.lastName[0]}
          </Avatar>
        )}

        <Paper
          p="sm"
          radius="lg"
          maw="70%"
          bg={isOwn ? 'blue.6' : undefined}
          c={isOwn ? 'white' : undefined}
          opacity={isDeleted ? 0.6 : 1}
          withBorder={!isOwn}
        >
          <Stack gap="xs">
            {isDeleted ? (
              <Text fs="italic" opacity={0.7}>
                Message deleted
              </Text>
            ) : (
              <>
                <Text
                  style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                >
                  {message.content}
                </Text>

                {message.attachments && message.attachments.length > 0 && (
                  <Stack gap="xs">
                    {message.attachments.map((attachment) => (
                      <AttachmentPreview
                        key={attachment.id}
                        attachment={attachment}
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}

            <Group justify="space-between" gap="xs">
              <Text opacity={0.7}>
                {dayjs(message.createdAt).format('HH:mm')}
              </Text>

              {isOwn && !isDeleted && onDelete && (
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color={isOwn ? 'white' : undefined}
                      opacity={0.7}
                    >
                      <IconDotsVertical size={14} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      onClick={() => onDelete(message.id)}
                    >
                      Poista
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Stack>
        </Paper>

        {isOwn && message.sender && (
          <Avatar color="blue" radius="xl" size={36}>
            {message.sender.firstName[0]}
            {message.sender.lastName[0]}
          </Avatar>
        )}
      </Group>
    </Stack>
  )
}
